import {Pattern, match} from 'ts-pattern';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import {useLazyCreateDataQueryQuery} from '@dms/api/core';
import {workshopRoutes} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {ApiError, composePath, Nullish} from 'shared/web';

import {
  WORKSHOP_SERVICE_CASE_GRID_CODE,
  WORKSHOP_SERVICE_ORDER_GRID_CODE,
} from '../constants/workshopOverviewTiles';
import {WorkshopOverviewTileConfig, WorkshopTileId} from '../types/WorkshopOverviewTileConfig';

const PREFER_CACHE_VALUE = true;

const DATA_GRID_DEEP_LINK_PARAM = 'dataGrids';
const DATA_GRID_DEFAULT_ROW_INDEX = '1';

function getTileGridRoute(gridCode: string) {
  return match(gridCode)
    .with(WORKSHOP_SERVICE_ORDER_GRID_CODE, () => workshopRoutes.serviceOrders)
    .with(WORKSHOP_SERVICE_CASE_GRID_CODE, () => workshopRoutes.serviceCases)
    .otherwise(() => workshopRoutes.overview);
}

export function useWorkshopTileRedirect() {
  const [createDataQuery] = useLazyCreateDataQueryQuery();
  const [pendingTileId, setPendingTileId] = useState<WorkshopTileId | Nullish>(null);

  const createTileDataQuery = (tile: WorkshopOverviewTileConfig) =>
    createDataQuery(
      {
        code: tile.gridCode,
        createDataQueryRequestBody: {filters: tile.filters, order: [], smartSearch: null},
      },
      PREFER_CACHE_VALUE
    );

  const prefetchTileQuery = (tile: WorkshopOverviewTileConfig) => {
    createTileDataQuery(tile);
  };

  const openTileGrid = (tile: WorkshopOverviewTileConfig) => {
    if (isNotNil(pendingTileId)) {
      return;
    }
    setPendingTileId(tile.id);

    // The tab must open synchronously inside the click gesture — opening it after
    // the request resolves would be popup-blocked. `noopener` would make
    // window.open return null, so it is omitted for this same-origin redirect.
    // Accepted residual risk: window.opener persists in the new tab even if it
    // later navigates cross-origin (mitigated app-wide by COOP, not per call site).
    const gridWindow = window.open(undefined, '_blank');

    createTileDataQuery(tile)
      .unwrap()
      .then((dataQuery) => {
        const url = composePath(getTileGridRoute(tile.gridCode), {
          queryParams: {
            [DATA_GRID_DEEP_LINK_PARAM]: `${tile.gridCode},${dataQuery.dataQueryId},${DATA_GRID_DEFAULT_ROW_INDEX}`,
          },
          // The deep link must not inherit whatever query params the homepage has.
          isSearchParamsPreserved: false,
        });

        match(gridWindow)
          .with(Pattern.nonNullable, (openedWindow) => openedWindow.location.assign(url))
          .otherwise(() => window.open(url, '_blank', 'noopener,noreferrer'));
      })
      .catch((error: ApiError) => {
        gridWindow?.close();
        handleApiError(error);
      })
      .finally(() => setPendingTileId(null));
  };

  return {prefetchTileQuery, openTileGrid, pendingTileId};
}
